/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

import * as Teams from '@microsoft/teams-js';
import * as React from 'react';

import { useHistory } from 'react-router-dom';

import { Constants } from '../../../common/Constants';
import { ApprovalRequestType } from '../../../data/types.generated';
import { EntryPoint, LogCategory, ScenarioName } from '../../../logger';
import { CreateEsignApprovalMobileComponent } from '../../../mobile/Esign/CreateEsignApproval/CreateEsignApprovalMobileComponent';
import { acceptedFileTypes, getAdobeFileService } from '../../../service/file-upload/AdobeFileService';
import { ApprovalCreator, ApprovalType } from '../../../service/gen';
import {
    AgreementState,
    ApprovalResponseType,
    CreateEnvelopeInput,
    EsignApprovalItem,
    ParticipantDetails,
    ParticipantRole,
    SignApprovalType,
} from '../../../service/gen/esign';
import { newGuid } from '../../../utilities/AppUtils';
import { getAppHost } from '../../../utilities/TeamsUtils';
import { delay } from '../../../utilities/Utils';
import { CreateEsignApprovalComponent } from '../CreateEsignApproval/CreateEsignApproval.component';
import { CreateEsignApprovalComponentProps } from '../CreateEsignApproval/CreateEsignApprovalComponentProps';
import { mobileOnCreateSuccess } from '../CreateEsignApproval/mobileOnCreateSuccess';
import { EsignIframeComponent } from '../EsignIframe/EsignIframe.component';
import { storeThatUserHasUsedEsign } from '../hooks/useCoachMarkState/useCoachMarkState';
import { Recipient } from '../RecipientList';
import { AdobeRequestDefaults, retrieveAdobeRequestDefaults } from '../utils/Authentication/AdobeAuthHelpers';
import { useCreateAdobeEnvelopeForm } from '../utils/forms';
import { createAdobeApproval } from './createAdobeApproval';

declare const MIDDLE_TIER_ENDPOINT: string;
export const envelopeInputKey = 'ComposeExtensionEnvelopeInput';

export interface CreateAdobeApprovalContainerProps {
    title: string;
    // Sets title if user switches between approval type
    setTitle: (value: string) => void;
    details: string;
    // Sets details if user switches between approval type
    setDetails: (value: string) => void;
    // Should show back button nav bar (applies for mobile personal app)
    showNavigationBar?: boolean;
    // Callback on dismissing the modal of this component when specified
    onDismiss?: () => void;
    // Set loading page in parent component
    setLoadingPage: (value: boolean) => void;
    // Set loading page message in parent component
    setLoadingPageMessage: (message: string) => void;
    // Set error page in parent component
    setErrorPage: (value: boolean) => void;
    // Set message on error page
    setErrorPageMessage?: (value: string) => void;
    // Set the page for Adobe sign login
    setLoginPage: (value: boolean) => void;
    // Callback on back button clicked
    onBack?: () => void;
    // Callback on iframe displayed
    onEsignIframeDisplay?: () => void;
    // The custom footer component which will be rendered in the modal footer
    customFooterComponent?: JSX.Element;
}

export const EnvelopeParticipantsMapper = (recipients: Recipient[], completeInOrder: boolean): ParticipantDetails[] => {
    const participantDetails = new Array<ParticipantDetails>();
    recipients.forEach((recipient: Recipient): void => {
        const participant: ParticipantDetails = {
            Email: recipient.persona.email,
            // If we need to complete in order then apply the order
            Order: completeInOrder ? Number.parseInt(recipient.order, 10) : 1,
            Role: recipient.role === 0 ? ParticipantRole.SIGNER : ParticipantRole.APPROVER,
            // If text is different than email then it contains the persons full name
            FullName: recipient.persona.email !== recipient.persona.text ? recipient.persona.text : null,
        };
        participantDetails.push(participant);
    });
    return participantDetails;
};

export const CreateAdobeApprovalContainer: React.FC<CreateAdobeApprovalContainerProps> = (
    props: CreateAdobeApprovalContainerProps
): JSX.Element => {
    const {
        title,
        setTitle,
        details,
        setDetails,
        onDismiss,
        showNavigationBar,
        setLoadingPage,
        setLoadingPageMessage,
        setErrorPage,
        setErrorPageMessage,
        onBack,
        customFooterComponent,
        onEsignIframeDisplay,
    } = props;

    const [esignIframeSrc, setEsignIframeSrc] = React.useState<string>(null);
    const form = useCreateAdobeEnvelopeForm({ title, setTitle, details, setDetails });
    const history = useHistory();

    const host = getAppHost();
    const loc = host.getLocale();
    const logger = host.getLogger();
    const fileUploadAcceptedTypes = acceptedFileTypes.join(',');
    const stageViewEnabled = host.getEcsConfig().MicrosoftTeamApprovals.AdobeStageViewEnabled;
    const fileService = getAdobeFileService();
    const logCategory = LogCategory.CreateEsignApprovalView;
    const entryPoint = onDismiss || showNavigationBar ? EntryPoint.PersonalApp : EntryPoint.ComposeExtension;

    /**
     * called when send or prepare button is pressed
     */
    const onNext = (isDraft: boolean): void => {
        storeThatUserHasUsedEsign();

        const clientRequestGUID = newGuid();

        if (setLoadingPage) {
            setLoadingPageMessage(loc.strings.approval_create_loading_text);
            setLoadingPage(true);
        }

        const titleString = title;
        const detailsString = details ?? '';
        const approvalResponseType = ApprovalResponseType.Basic;
        const approvalType = ApprovalType.ESign;
        const participantDetails = EnvelopeParticipantsMapper(
            form.recipientsInput.value,
            form.completeInOrderInput.value
        );

        const approverNamesText = new Array<string>();
        const approversUserObjectId = new Array<string>();

        const esignItem: EsignApprovalItem = {
            TransientDocumentId: form.filesInput.value[0].transientDocumentId,
            LibraryDocumentId: form.filesInput.value[0].libraryDocumentId,
        };

        logger.logTraceInfo(logCategory, `Clicked on create approval submit, approval type ${approvalType}`);

        retrieveAdobeRequestDefaults().then((adobeRequestDefaults: AdobeRequestDefaults): void => {
            const userRoles = form.recipientsInput.value.map((recipient: Recipient): string => {
                return recipient.role === ParticipantRole.SIGNER ? 'SIGNER' : 'APPROVER';
            });

            logger.logCreateEsignApproval({
                titleSize: title.length,
                numberOfApprovers: form.recipientsInput.value.length,
                detailsSize: details?.length ?? 0,
                clientRequestId: clientRequestGUID,
                correlationId: clientRequestGUID,
                hasItem: !!esignItem,
                itemType:
                    form.filesInput.value &&
                    form.filesInput.value.length > 0 &&
                    form.filesInput.value[0].transientDocumentId
                        ? 'desktopDocument'
                        : 'AdobeCloudDocument',
                itemSize: '0',
                signApprovalType: 'Adobe',
                userRoles: userRoles.toString(),
                completeInOrder: form.completeInOrderInput.value,
                isDraft,
            });

            const data: CreateEnvelopeInput = {
                CallbackUrls: [MIDDLE_TIER_ENDPOINT + Constants.WEBHOOK_ENDPOINT],
                Creator:
                    entryPoint === EntryPoint.PersonalApp ? ApprovalCreator.PersonalApp : ApprovalCreator.ChatMessage,
                AssignedTo: approversUserObjectId,
                ApproverNames: approverNamesText,
                Type: approvalResponseType,
                ClientRequestId: clientRequestGUID,
                Title: titleString.trim(),
                Details: detailsString.trim(),
                EsignItem: esignItem,
                Priority: null,
                ParticipantDetails: participantDetails,
                EsignToken: adobeRequestDefaults.token,
                ApiEndpoint: adobeRequestDefaults.apiAccessPoint,
                SignApprovalType: SignApprovalType.Adobe,
                State: isDraft ? AgreementState.Authoring : AgreementState.In_Process,
                ApprovalType: ApprovalType.ESign,
                // FirstTimeProvisioning is NYI for Adobe as of now, but something that may come in handy later.
                FirstTimeProvisioning: false,
                UserName: host.getUserPrincipalName(),
            };

            if (entryPoint === EntryPoint.PersonalApp) {
                createAdobeApproval(
                    data,
                    setEsignIframeSrc,
                    setLoadingPage,
                    setLoadingPageMessage,
                    setErrorPage,
                    setErrorPageMessage
                ).then((): void => {
                    if (showNavigationBar) {
                        mobileOnCreateSuccess(history);
                    } else if (isDraft && stageViewEnabled) {
                        onEsignIframeDisplay();
                    } else {
                        onDismiss();
                    }
                });
            } else {
                // Save envelope input data in localStorage for later retrieval, as this current context will be lost.
                // The call to dialog submit will trigger a TaskModuleContinueResponse which navigates directly to this component, without its parent.
                localStorage.setItem(envelopeInputKey, JSON.stringify(data));
                logger.logTraceInfo(logCategory, 'Triggering background Approvals creation using bot');
                logger.logTraceDebug(logCategory, 'CreateEnvelope:onNext end for chat/channel');
                setLoadingPageMessage(loc.strings.approval_create_loading_text);
                delay(Constants.screenReaderWaitTime).then((): void => {
                    logger.flushLogs((): void => Teams.dialog.url.submit(data));
                });
            }
        });
    };

    const componentProps: CreateEsignApprovalComponentProps = {
        form,
        fileService,
        onNext,
        fileUploadAcceptedTypes,
        onBack,
        customFooterComponent,
        signApprovalType: SignApprovalType.Adobe,
        stageViewEnabled,
    };

    const createComponent = host.isMobile() ? (
        <CreateEsignApprovalMobileComponent {...componentProps} />
    ) : (
        <CreateEsignApprovalComponent {...componentProps} />
    );

    return esignIframeSrc != null && stageViewEnabled ? (
        <EsignIframeComponent
            showHeader={false}
            src={esignIframeSrc}
            onMessageProcessed={onDismiss}
            approvalType={ApprovalRequestType.ESign}
            scenarioName={ScenarioName.CreateEsignApproval}
        />
    ) : (
        createComponent
    );
};
